import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  alpha,
  useTheme,
  darken,
} from '@mui/material'
import {
  Close as CloseIcon,
  WarningRounded as WarningIcon,
  HelpOutlineRounded as HelpIcon,
  ErrorOutlineRounded as ErrorIcon,
} from '@mui/icons-material'

interface AreYouSurePopupProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  message?: string | React.ReactNode
  confirmText?: string
  cancelText?: string
  isProcessing?: boolean
  color?: string
  type?: 'warning' | 'question' | 'danger'
}

/**
 * A reusable confirmation dialog with a modern, premium design.
 * 
 * @param open - Controls whether the dialog is shown
 * @param onClose - Function to call when the dialog is cancelled or closed
 * @param onConfirm - Function to call when the user confirms the action
 * @param title - The main heading of the dialog
 * @param message - The descriptive text or component below the title
 * @param confirmText - Text for the confirmation button
 * @param cancelText - Text for the cancellation button
 * @param isProcessing - If true, shows a loading state and disables actions
 * @param color - The MUI color palette to use for the confirm button
 * @param type - The preset style/icon (warning, question, danger)
 */
const AreYouSurePopup: React.FC<AreYouSurePopupProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message = 'This action cannot be undone and may affect your data.',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isProcessing = false,
  color = 'primary',
  type = 'warning',
}) => {
  const theme = useTheme()

  // Helper to determine the main theme color based on props
  const getThemeColor = React.useMemo(() => {
    // 1. If color is a recognized MUI palette color, use it
    if (color && (theme.palette as any)[color]) {
      return (theme.palette as any)[color].main
    }
    // 2. If it's a custom string (e.g., 'blue', '#ff0000'), return it
    if (color && color !== 'primary') return color

    // 3. Fallback to colors based on type
    if (type === 'danger') return theme.palette.error.main
    if (type === 'question') return theme.palette.info.main
    return theme.palette.warning.main
  }, [color, type, theme])

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return <ErrorIcon sx={{ fontSize: 40, color: getThemeColor }} />
      case 'question':
        return <HelpIcon sx={{ fontSize: 40, color: getThemeColor }} />
      case 'warning':
      default:
        return <WarningIcon sx={{ fontSize: 40, color: getThemeColor }} />
    }
  }

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      {/* Upper accent bar for premium look */}
      <Box sx={{ height: 6, width: '100%', bgcolor: getThemeColor }} />

      <DialogTitle
        sx={{
          m: 0,
          p: 1.5,
          pb: 0,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isProcessing}
          sx={{
            color: theme.palette.grey[400],
            '&:hover': {
              color: theme.palette.grey[600],
              bgcolor: alpha(theme.palette.grey[200], 0.4),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pb: 1, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: alpha(getThemeColor, 0.1),
              mb: 0.5,
            }}
          >
            {getIcon()}
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              {message}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 4,
          pt: 3,
          gap: 2,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          disabled={isProcessing}
          sx={{
            py: 1.2,
            borderRadius: 2.5,
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.secondary,
            fontWeight: 600,
            fontSize: '0.95rem',
            '&:hover': {
              borderColor: theme.palette.grey[400],
              bgcolor: theme.palette.grey[50],
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          fullWidth
          disabled={isProcessing}
          sx={{
            py: 1.2,
            borderRadius: 2.5,
            bgcolor: getThemeColor,
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.95rem',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: darken(getThemeColor, 0.1),
              boxShadow: '0 4px 12px ' + alpha(getThemeColor, 0.3),
            },
          }}
        >
          {isProcessing ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            confirmText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AreYouSurePopup
